import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useStarredProjects } from '../hooks/useStarredProjects';
import { TierBadge } from '../components/TierBadge';
import { Star, Folders, ExternalLink } from 'lucide-react';
import { SubscriptionTier } from '../lib/supabase';

export function StarredPage() {
  const { starredProjects, loading, toggleStar, fetchStarred, isStarred } = useStarredProjects();

  useEffect(() => {
    document.title = 'Starred Apps - Llambada';
    fetchStarred();
    return () => { document.title = 'Llambada'; };
  }, [fetchStarred]);

  const getProjectUrl = (project: { public_id: string | null; slug: string | null }) => {
    return `/p/${project.public_id}${project.slug ? `/${project.slug}` : ''}`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        <div className="text-center mb-6 sm:mb-10">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-theme-text mb-2">
            Starred Apps
          </h1>
          <p className="text-theme-text-secondary">
            Your bookmarked mini-apps
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : starredProjects.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-theme-surface mb-6">
              <Star className="h-8 w-8 text-theme-text-muted" />
            </div>
            <h3 className="font-display text-xl font-semibold text-theme-text mb-2">
              No starred apps yet
            </h3>
            <p className="text-theme-text-secondary mb-6">
              Browse public mini-apps and star the ones you like to save them here.
            </p>
            <Link to="/" className="btn btn-primary">
              Browse Mini-Apps
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {starredProjects.map((project) => (
              <Link
                key={project.id}
                to={getProjectUrl(project)}
                className="card p-6 hover:border-accent-500/50 transition-all duration-200 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500/20 to-cyan-500/20 group-hover:from-accent-500/30 group-hover:to-cyan-500/30 transition-colors">
                    <Folders className="h-6 w-6 text-accent-500" />
                  </div>
                  <div className="flex items-center gap-2">
                    {project.required_tier && (
                      <TierBadge tier={project.required_tier as SubscriptionTier} size="sm" />
                    )}
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleStar(project.project_id); }}
                      className="p-0.5 rounded transition-colors hover:bg-theme-surface-hover"
                      title={isStarred(project.project_id) ? 'Unstar' : 'Star'}
                    >
                      <Star
                        className={`h-4 w-4 transition-colors ${
                          isStarred(project.project_id) ? 'text-amber-500' : 'text-theme-text-muted'
                        }`}
                        fill={isStarred(project.project_id) ? 'currentColor' : 'none'}
                      />
                    </button>
                    <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-accent-500 transition-colors" />
                  </div>
                </div>
                <h3 className="font-display text-lg font-semibold text-theme-text mb-2 group-hover:text-accent-500 transition-colors">
                  {project.name}
                </h3>
                {project.description && (
                  <p className="text-theme-text-secondary text-sm line-clamp-2 mb-3">
                    {project.description}
                  </p>
                )}
                <p className="text-xs text-theme-text-muted">
                  Starred {formatDate(project.starred_at)}
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
